import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/Button';
import { Select } from '../../../../components/forms/Select';
import { useCalendarIntegrations } from '../../../../hooks/useCalendarIntegrations';
import { SchedulingProfile } from '../types';
import { useProfileSave } from '../useProfileSave';

export const SyncTab: React.FC<{ profile: SchedulingProfile }> = ({ profile }) => {
  const { t } = useTranslation();
  const { save, isSaving, fieldErrors, formError } = useProfileSave(profile.id);
  const { data: calendars = [] } = useCalendarIntegrations();
  const [syncCalendar, setSyncCalendar] = useState(profile.sync_calendar?.id ?? '');

  const options = [
    { value: '', label: t('schedulingAdmin.sync.none') },
    ...calendars.map(calendar => {
      // A calendar assigned to an agent cannot be a sync target (the backend rejects it).
      const agents = calendar.agents ?? [];
      const taken = agents.length > 0 && calendar.id !== profile.sync_calendar?.id;
      return {
        value: calendar.id,
        label: `${calendar.name || calendar.provider_display} – ${calendar.email}`,
        disabled: taken,
        description: taken
          ? t('schedulingAdmin.sync.usedByAgent', {
              names: agents.map(agent => agent.name).join(', '),
            })
          : undefined,
      };
    }),
  ];

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-sm text-gray-600 dark:text-gray-400">{t('schedulingAdmin.sync.intro')}</p>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('schedulingAdmin.sync.calendar')}
        </label>
        <Select
          options={options}
          value={syncCalendar}
          onChange={value => setSyncCalendar(value as string)}
          error={!!fieldErrors.sync_calendar}
          errorText={fieldErrors.sync_calendar}
        />
        <p className="text-xs text-gray-500 mt-2">{t('schedulingAdmin.sync.hint')}</p>
        <p className="text-xs text-gray-500 mt-1">
          <Link
            to="/calendars?area=external"
            className="text-blue-600 hover:underline dark:text-blue-400"
          >
            {t('schedulingAdmin.sync.connect')}
          </Link>
        </p>
      </div>

      {formError && <p className="text-sm text-red-600">{formError}</p>}
      <Button
        onClick={() => save({ sync_calendar: syncCalendar || null })}
        loading={isSaving}
        disabled={syncCalendar === (profile.sync_calendar?.id ?? '')}
      >
        {t('common.save')}
      </Button>
    </div>
  );
};
